import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * Phase 94 — TopBar right-cluster slot Context.
 *
 * Solves the createPortal race that bit Phase 92: any IIFE inside JSX
 * that called `document.getElementById('topbar-right-slot')` + ReactDOM
 * .createPortal crashed on the lazy chunk's first render because the
 * sibling TopBar hadn't committed yet. React 18 StrictMode detected the
 * invalid container and unmounted the whole subtree.
 *
 * The fix: TopBar exposes its slot via Context AFTER mount (post-commit).
 * Consumers read the slot from Context — which is guaranteed to be a real
 * Element (or null while TopBar is still loading). The portal call only
 * fires when the slot is a real Element, eliminating the race entirely.
 *
 * Usage:
 *   // AppShell — provide
 *   <TopBarRightSlotProvider>
 *     <AppShell />
 *   </TopBarRightSlotProvider>
 *
 *   // TopBar — register slot ref on mount
 *   const ref = useRef(null);
 *   const { setSlot } = useTopBarRightSlot();
 *   useEffect(() => { setSlot(ref.current); return () => setSlot(null); }, []);
 *   return <header>...<div ref={ref} id="topbar-right-slot" /></header>;
 *
 *   // Consumer — read slot
 *   const slot = useTopBarRightSlot();
 *   return slot ? createPortal(<Btn/>, slot) : null;
 */
const TopBarRightSlotContext = createContext(null);

export function TopBarRightSlotProvider({ children }) {
  const [slot, setSlotState] = useState(null);
  // useCallback so consumers reading setSlot via context don't trigger
  // unnecessary re-renders when the function reference changes.
  const setSlot = useCallback((el) => {
    setSlotState((prev) => (prev === el ? prev : el));
  }, []);
  const value = useMemo(() => ({ slot, setSlot }), [slot, setSlot]);
  return (
    <TopBarRightSlotContext.Provider value={value}>
      {children}
    </TopBarRightSlotContext.Provider>
  );
}

export function useTopBarRightSlot() {
  const ctx = useContext(TopBarRightSlotContext);
  // Returning null (not the whole context) keeps the consumer API simple:
  // `const slot = useTopBarRightSlot()` returns the element or null.
  return ctx ? ctx.slot : null;
}

export function useTopBarRightSlotSetter() {
  const ctx = useContext(TopBarRightSlotContext);
  return ctx ? ctx.setSlot : null;
}
